import { getDocs, collection, doc, getDoc } from "firebase/firestore";
import { db } from '../../../firebase/config';

export type Company = {
    id: string;
    bankId: string;
    companyName: string;
    companyType?: string;
    companyId?: string;
    companyNo?: string;
    email?: string;
    companyAddress?: string;
    contactPersonName?: string;
    contactMobileNumber?: string;
    contactEmail?: string;
    status?: string;
    isSelectedCompany?: boolean;
    createdAt?: any;
    updatedAt?: any;
};

export async function fetchCompaniesData(uid: string, isBankUser: boolean = false): Promise<Company[]> {
    // Find bankId by checking which bank has this PSO, or if user is bank, use uid directly
    let bankId: string | null = null;

    if (isBankUser) {
        // For bank users, check if uid is a bank document ID or if there's a bank with userId == uid
        const directBank = await getDoc(doc(db, "banks", uid));
        if (directBank.exists()) {
            bankId = uid;
        } else {
            // Try query by userId field
            const banksSnap = await getDocs(collection(db, "banks"));
            for (const bankDoc of banksSnap.docs) {
                const bankData = bankDoc.data();
                if (bankData.userId === uid) {
                    bankId = bankDoc.id;
                    break;
                }
            }
        }
    } else {
        // For PSO users, find which bank has this PSO
        const banksSnap = await getDocs(collection(db, "banks"));
        for (const bankDoc of banksSnap.docs) {
            const posDocRef = doc(db, "banks", bankDoc.id, "pos", uid);
            const posDoc = await getDoc(posDocRef);
            if (posDoc.exists()) {
                bankId = bankDoc.id;
                break;
            }
        }
    }

    if (!bankId) {
        console.log('No bank found for user:', uid);
        return [];
    }

    // Fetch companies from banks/{bankId}/companies
    const companiesSnap = await getDocs(collection(db, "banks", bankId, "companies"));
    console.log(`Found ${companiesSnap.docs.length} companies in banks/${bankId}/companies`);

    const companiesList: any[] = companiesSnap.docs.map(doc => ({
        id: doc.id,
        bankId: bankId!,
        ...doc.data()
    }));

    console.log('Companies from bank collection:', companiesList);

    // Fetch details for companies that have companyId from global collection
    const enrichedCompanies = await Promise.all(
        companiesList.map(async (company) => {
            console.log('Processing company:', company.id, 'companyId:', company.companyId);

            // Check if this company has a companyId to fetch from global collection
            const hasCompanyId = company.companyId && typeof company.companyId === 'string';

            if (hasCompanyId) {
                try {
                    console.log(`Fetching global company with ID: ${company.companyId}`);
                    // Fetch company details from global collection
                    const globalCompanyDoc = await getDoc(doc(db, "companies", company.companyId));
                    if (globalCompanyDoc.exists()) {
                        const globalData = globalCompanyDoc.data();
                        console.log(`Found global company: ${globalData.companyName}`, globalData);

                        // Determine companyType: 
                        // - Use bank's companyType if it exists
                        // - If bank's companyType is missing:
                        //   - If global's type is CCTV or BAMS, default to "Guard Company"
                        //   - Otherwise, use global's type or default to "Guard Company"
                        let finalCompanyType: string;
                        if (company.companyType) {
                            // Bank's document has companyType, use it
                            finalCompanyType = company.companyType;
                        } else {
                            // Bank's document is missing companyType
                            if (globalData.companyType === 'CCTV' || globalData.companyType === 'BAMS') {
                                // If global type is CCTV or BAMS, default to Guard Company
                                finalCompanyType = 'Guard Company';
                            } else {
                                // Otherwise use global's type or default to Guard Company
                                finalCompanyType = globalData.companyType || 'Guard Company';
                            }
                        }

                        const enrichedCompany = {
                            ...company,
                            companyName: globalData.companyName || company.companyName,
                            companyNo: globalData.companyNo || company.companyNo,
                            email: globalData.email || company.email,
                            companyAddress: globalData.companyAddress || company.companyAddress,
                            companyType: finalCompanyType,
                            // Use contact details from bank's document (company) if available, otherwise from global
                            contactPersonName: company.contactPersonName || globalData.contactPersonName,
                            contactMobileNumber: company.contactMobileNumber || globalData.contactMobileNumber,
                            contactEmail: company.contactEmail || globalData.contactEmail,
                            companyImg: globalData.imgUrl || globalData.companyImg,
                            isSelectedCompany: true
                        } as Company;

                        console.log('Enriched company:', enrichedCompany);
                        return enrichedCompany;
                    } else {
                        console.log(`Global company not found for ID: ${company.companyId}`);
                        // Global company not found, return bank's data as is
                        return {
                            ...company,
                            companyType: company.companyType || 'Guard Company',
                            isSelectedCompany: false
                        } as Company;
                    }
                } catch (err) {
                    console.error(`Failed to fetch global company ${company.companyId}:`, err);
                    // Return bank's data if fetch fails
                    return {
                        ...company,
                        companyType: company.companyType || 'Guard Company',
                        isSelectedCompany: false
                    } as Company;
                }
            }

            // Company doesn't have companyId, return as is (manual entry)
            console.log('Company without companyId (manual entry):', company);
            return {
                ...company,
                companyType: company.companyType || 'Guard Company',
                isSelectedCompany: false
            } as Company;
        })
    );

    // Filter out companies without companyName (they can't be displayed properly)
    const validCompanies = enrichedCompanies.filter(company => company.companyName && company.companyName.trim() !== '');

    // Sort by company name
    validCompanies.sort((a, b) => {
        const nameA = a.companyName || '';
        const nameB = b.companyName || '';
        return nameA.localeCompare(nameB);
    });

    console.log('Total companies fetched:', enrichedCompanies.length);
    console.log('Valid companies (with name):', validCompanies.length);
    console.log('Final companies list:', validCompanies);

    return validCompanies;
}

export async function fetchCompanyBankId(uid: string, isBankUser: boolean = false): Promise<string | null> {
    console.log('fetchCompanyBankId called with uid:', uid, 'isBankUser:', isBankUser);

    if (isBankUser) {
        // For bank users, check if uid is a bank document ID or if there's a bank with userId == uid
        console.log('Checking for bank user...');
        const directBank = await getDoc(doc(db, "banks", uid));
        if (directBank.exists()) {
            console.log('Found bank with direct ID:', uid);
            return uid;
        }
        // Try query by userId field
        const banksSnap = await getDocs(collection(db, "banks"));
        console.log('Checking banks by userId, found', banksSnap.docs.length, 'banks');
        for (const bankDoc of banksSnap.docs) {
            const bankData = bankDoc.data();
            if (bankData.userId === uid) {
                console.log('Found bank by userId:', bankDoc.id);
                return bankDoc.id;
            }
        }
        console.log('No bank found for bank user');
        return null;
    } else {
        // For PSO users, find which bank has this PSO
        console.log('Checking for PSO user...');
        const banksSnap = await getDocs(collection(db, "banks"));
        console.log('Checking', banksSnap.docs.length, 'banks for PSO:', uid);
        for (const bankDoc of banksSnap.docs) {
            const posDocRef = doc(db, "banks", bankDoc.id, "pos", uid);
            const posDoc = await getDoc(posDocRef);
            console.log(`Checking banks/${bankDoc.id}/pos/${uid}:`, posDoc.exists());
            if (posDoc.exists()) {
                console.log('Found bank for PSO:', bankDoc.id);
                return bankDoc.id;
            }
        }
        console.log('No bank found for PSO user');
        return null;
    }
}

export async function fetchGlobalCompanies(companyType?: string): Promise<Company[]> {
    const companiesSnap = await getDocs(collection(db, "companies"));
    let companiesList = companiesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
    } as Company));

    if (companyType) {
        companiesList = companiesList.filter(company => company.companyType === companyType);
    }

    return companiesList;
}

